import { useState } from "react";
import { X, KeyRound, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff, Smartphone } from "lucide-react";
import { api, pesanGalat } from "../lib/api";
import { ATURAN_PASSWORD, PASSWORD_MIN, periksaPasswordBaru } from "../lib/password";

/**
 * Modal Lupa Password — reset mandiri dengan kode 6 digit Google Authenticator
 * (keputusan owner 24 Sep 2026; backend `routers/authenticator.py`).
 *
 * Jalur lama (6 digit terakhir NIK) sudah dimatikan 20 Sep 2026 karena NIK
 * bukan rahasia. Pengguna yang belum pernah menyambungkan authenticator tetap
 * direset superadmin/admin — teks bantuan di bawah form menyebutnya, supaya
 * penolakan server tidak jadi jalan buntu.
 *
 * Server sengaja membalas kalimat yang SAMA untuk "akun tidak ada", "belum
 * tersambung", dan "kode salah" (anti menebak akun). Jangan menambah tebakan
 * sendiri di layar ini — tampilkan pesan server apa adanya.
 */
export default function ForgotPasswordModal({ onClose, onBerhasil }) {
  const [akun, setAkun] = useState("");
  const [kode, setKode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [lihat, setLihat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [galat, setGalat] = useState("");
  const [sukses, setSukses] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setGalat("");
    if (!/^\d{6}$/.test(kode)) { setGalat("Kode harus 6 digit angka dari aplikasi Google Authenticator."); return; }
    const galatPw = periksaPasswordBaru(pw);
    if (galatPw) { setGalat(galatPw); return; }
    if (pw !== pw2) { setGalat("Ulangi password baru belum sama."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password-authenticator", {
        email_atau_username: akun.trim(), kode, password_baru: pw,
      });
      setSukses(data?.message || "Password berhasil diganti. Silakan login dengan password baru.");
      onBerhasil?.(akun.trim());
    } catch (err) {
      setGalat(pesanGalat(err, "Gagal memproses. Coba lagi."));
      // Kode yang sudah dikirim tidak bisa dipakai dua kali — kosongkan supaya
      // pengguna mengetik kode TERBARU dari aplikasi, bukan menekan ulang.
      setKode("");
    } finally {
      setLoading(false);
    }
  };

  const kelasInput = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="forgot-password-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden my-auto">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 grid place-items-center">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-lg">Lupa Password?</h3>
              <p className="text-[11px] text-emerald-100 mt-0.5">Reset sendiri dengan kode Google Authenticator</p>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="forgot-close" aria-label="Tutup"
            className="text-white/80 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sukses ? (
          <div className="p-6 text-center" data-testid="forgot-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-sm text-slate-800 font-medium mb-4">{sukses}</p>
            <button type="button" onClick={onClose} data-testid="forgot-back-to-login"
              className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm">
              Kembali ke Login
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex gap-2">
              <Smartphone className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
              <p>
                Buka aplikasi <b>Google Authenticator</b> di HP Anda, lalu salin kode 6 digit untuk akun
                ini. Kode berganti tiap 30 detik — pakai yang sedang tampil.
              </p>
            </div>

            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Username atau email</span>
              <input type="text" required value={akun} onChange={e => setAkun(e.target.value)}
                autoCapitalize="none" autoComplete="username" spellCheck={false}
                data-testid="forgot-email" placeholder="username Anda" className={kelasInput} />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Kode Google Authenticator</span>
              <input type="text" required value={kode} maxLength={6} inputMode="numeric" autoComplete="one-time-code"
                onChange={e => setKode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="forgot-kode" placeholder="123456"
                className={`${kelasInput} font-mono tracking-[0.4em]`} />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Password baru</span>
              <div className="relative">
                <input type={lihat ? "text" : "password"} required value={pw} onChange={e => setPw(e.target.value)}
                  autoComplete="new-password" data-testid="forgot-password-baru" placeholder={`minimal ${PASSWORD_MIN} karakter`}
                  className={`${kelasInput} pr-11`} />
                <button type="button" onClick={() => setLihat(v => !v)} data-testid="forgot-lihat-password"
                  aria-label={lihat ? "Sembunyikan password" : "Lihat password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-700">
                  {lihat ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <span className="block text-[11px] text-slate-500 mt-1" data-testid="aturan-password">{ATURAN_PASSWORD}</span>
            </label>

            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Ulangi password baru</span>
              <input type={lihat ? "text" : "password"} required value={pw2} onChange={e => setPw2(e.target.value)}
                autoComplete="new-password" data-testid="forgot-password-ulang" className={kelasInput} />
            </label>

            {galat && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700 flex items-start gap-2"
                data-testid="forgot-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{galat}</span>
              </div>
            )}

            <button type="submit" disabled={loading || !akun.trim() || kode.length !== 6 || !pw || !pw2}
              data-testid="forgot-submit"
              className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Ganti Password
            </button>

            <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="forgot-bantuan">
              <b>Belum menyambungkan Google Authenticator?</b> Minta admin atau superadmin mereset password Anda.
              Setelah bisa masuk, sambungkan di <b>Pengaturan Saya → Password &amp; Keamanan</b> supaya lain kali
              bisa reset sendiri.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
